import React, { useState, useEffect, useRef } from "react";
import { Link } from "../i18n/LangContext";
import { useTranslation } from "react-i18next";
import { FaRocket, FaBrain, FaCubes, FaArrowRight } from "react-icons/fa";
import Methodology from "../components/Methodology";
import CtaPair from "../components/CtaPair";
import QuickContact from "../components/QuickContact";
import Seo from "../seo/Seo";

import AtevLogo from "../assets/images/atev.png";
import HP from "../assets/images/hp.png";
import Verlag from "../assets/images/verlag.webp";
import Izodek from "../assets/images/izodeko.webp";
import Mndwrk from "../assets/images/mndwrk.webp";
import Jao from "../assets/images/jao.jpg";
import Termometal from "../assets/images/termometal.jpg";
import Fpi from "../assets/images/fpi.webp";
import Informatika from "../assets/images/99999informatika.webp";
import Y2y from "../assets/images/y2y.png";
import ApexLab from "../assets/images/apexlab.jpg";
import Slider3 from "../assets/images/slider-img-3.webp";
import Slider2 from "../assets/images/slider-img-2.webp";
import Slider1 from "../assets/images/slider-img-1.webp";

// width/height are the files' intrinsic sizes: they let the browser reserve
// the box before the image arrives, so the hero does not jump (CLS).
const SLIDES = [
  { img: Slider1, w: 579, h: 519, badge: true },
  { img: Slider2, w: 900, h: 600 },
  { img: Slider3, w: 900, h: 900 },
];

const Home = () => {
  const { t } = useTranslation();
  const slides = t("home.slides");
  const faq = t("home.faq");
  // Short answers about the free POC. FAQPage markup is what search engines
  // show as expandable results and what AI assistants quote most readily.
  const faqJsonLd = {
    "@type": "FAQPage",
    mainEntity: faq.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
  const [activeSlide, setActiveSlide] = useState(0);
  const rocketRef = useRef(null);
  const [isRocketLaunching, setIsRocketLaunching] = useState(false);

  // Auto slide. Starts only after the visitor first scrolls, taps or presses a
  // key, pauses while the pointer or keyboard focus is in the hero, and never
  // runs under prefers-reduced-motion. So the first slide - the free POC and the
  // booking button - stays put until someone engages; WCAG 2.2.2 gets a way to
  // stop the movement; and Largest Contentful Paint is not re-triggered by a
  // slide swap every five seconds (it was measuring 10.5 s on mobile for that
  // reason alone).
  const [autoplay, setAutoplay] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const start = () => setAutoplay(true);
    const events = ["scroll", "pointerdown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, start, { once: true, passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, start));
  }, []);

  useEffect(() => {
    if (!autoplay || paused) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev === SLIDES.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [activeSlide, autoplay, paused]);

  // Rocket launch observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Delay launch slightly so user sees it before it flies away
          setTimeout(() => setIsRocketLaunching(true), 1500);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (rocketRef.current) {
      observer.observe(rocketRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSlideChange = (index) => {
    setActiveSlide(index);
  };

  return (
    <div className="home-page-container">
      <Seo page="home" path="/" override={{ jsonLd: [faqJsonLd] }} />
      <div className="hero_area">
        {/* Slider section */}
        <section
          className="slider_section"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <div className="carousel_btn-container">
            <button
              type="button"
              className="carousel-control-prev"
              aria-label={t("home.prev")}
              onClick={() => handleSlideChange(activeSlide === 0 ? SLIDES.length - 1 : activeSlide - 1)}
            >
              <span className="sr-only">{t("home.prev")}</span>
            </button>
            <button
              type="button"
              className="carousel-control-next"
              aria-label={t("home.next")}
              onClick={() => handleSlideChange(activeSlide === SLIDES.length - 1 ? 0 : activeSlide + 1)}
            >
              <span className="sr-only">{t("home.next")}</span>
            </button>
          </div>

          <div className="carousel slide">
            <ol className="carousel-indicators">
              {SLIDES.map((_, num) => (
                <li key={num} className={activeSlide === num ? "active" : ""}>
                  <button
                    type="button"
                    onClick={() => handleSlideChange(num)}
                    aria-label={slides[num].alt}
                    aria-current={activeSlide === num ? "true" : undefined}
                  >
                    0{num + 1}
                  </button>
                </li>
              ))}
            </ol>

            <div className="carousel-inner">
              {SLIDES.map((slide, i) => (
                <div key={i} className={`carousel-item ${activeSlide === i ? "active" : ""}`}>
                  <div className="container-fluid">
                    <div className="row align-items-center">
                      <div className="col-md-5 offset-md-1">
                        <div className="detail-box">
                          {slide.badge && (
                            <span className="hero-badge">{t("home.badge")}</span>
                          )}
                          <h1 className="display-4 font-weight-bold">
                            {slides[i].title1} <br />
                            {slides[i].title2}
                          </h1>
                          <p className="lead mt-3 mb-4">{slides[i].text}</p>
                          <div className="btn-box">
                            <CtaPair tone="dark" />
                          </div>
                        </div>
                      </div>
                      <div className="offset-md-1 col-md-4 img-container">
                        <div className="img-box">
                          <img
                            src={slide.img}
                            alt={slides[i].alt}
                            className="img-fluid floating-img yolo-cyber-pop"
                            width={slide.w}
                            height={slide.h}
                            // the first slide is the largest paint; the hidden ones can wait
                            loading={i === 0 ? "eager" : "lazy"}
                            fetchPriority={i === 0 ? "high" : "low"}
                            decoding="async"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Creative Teaser: Expertise & Work */}
      <section className="expertise-teaser-section py-5 bg-light">
        <div className="container py-4">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="font-weight-bold mb-4">{t("home.expertise.title")}</h2>
              <p className="text-muted mb-4 lead">
                {t("home.expertise.text")}
              </p>
              <Link to="/offers" className="cta-inline">
                {t("cta.seeOffers")} <FaArrowRight />
              </Link>
            </div>
            <div className="col-lg-5 offset-lg-1">
              <div className="row g-3">
                <div className="col-6">
                  <div className="p-4 bg-white rounded-4 shadow-sm text-center transition-hover h-100">
                    <FaBrain className="text-primary mb-3" size={40} />
                    <h5 className="font-weight-bold">{t("home.expertise.card1")}</h5>
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-4 bg-white rounded-4 shadow-sm text-center transition-hover h-100 mt-4">
                    <FaCubes className="text-primary mb-3" size={40} />
                    <h5 className="font-weight-bold">{t("home.expertise.card2")}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Methodology />

      {/* Creative Teaser: Playground */}
      <section className="playground-teaser-section py-5 bg-gradient-modern text-white" style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%)' }}>
        <div className="container text-center py-5">
          <div ref={rocketRef} style={{ display: 'inline-block', position: 'relative', zIndex: 9999 }}>
            <FaRocket className={`text-info mb-4 ${isRocketLaunching ? 'yolo-launch' : ''}`} size={50} />
          </div>
          <h2 className="display-5 font-weight-bold mb-3 text-white">{t("home.playground.title")}</h2>
          <p className="lead mb-5 max-w-700 mx-auto text-light opacity-75">
            {t("home.playground.text")}
          </p>
          <Link to="/playground" className="cta-inline cta-inline--light">
            {t("cta.seeWork")} <FaArrowRight />
          </Link>
        </div>
      </section>

      {/* Companies section */}
      <section className="companies_section py-5">
        <div className="container py-4">
          <div className="text-center mb-5">
            <h2 className="font-weight-bold">{t("home.companies.title")}</h2>
            <p className="text-muted">{t("home.companies.subtitle")}</p>
          </div>
          <div className="companies_grid">
            {[
              { img: Jao, name: 'JAO', style: { borderRadius: "100px", transform: "scale(1.4)" } },
              { img: Fpi, name: 'FPI' },
              { img: Termometal, name: 'Termometal' },
              { img: Informatika, name: '99999 Informatika' },
              { img: Y2y, name: 'Y2Y' },
              { img: ApexLab, name: 'Apex Lab' },
              { img: Mndwrk, name: 'MndWrk' },
              { img: Izodek, name: 'Izodeko', style: { borderRadius: "100px", transform: "scale(1.4)" } },
              { img: Verlag, name: 'Verlag Dashöfer' },
              { img: HP, name: 'Human Priority', style: { borderRadius: "100px", transform: "scale(1.4)" } },
              { img: AtevLogo, name: 'Atev' }
            ].map((company, idx) => (
              <div key={idx} className="company_card transition-hover">
                <div className="logo_container">
                  <img src={company.img} alt={t("home.companies.logoAlt", { name: company.name })} style={company.style} loading="lazy" decoding="async" />
                </div>
                <h6 className="mt-3 font-weight-bold text-muted">{company.name}</h6>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ about the free proof of concept */}
      <section className="home-faq py-5">
        <div className="container py-4">
          <div className="text-center mb-5">
            <h2 className="font-weight-bold">{faq.title}</h2>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-9">
              {faq.items.map((item, i) => (
                <details key={i} className="faq-item modern-service-card rounded-4 shadow-sm mb-3 p-4">
                  <summary className="h5 font-weight-bold mb-0" style={{ cursor: "pointer" }}>{item.q}</summary>
                  <p className="text-muted mt-3 mb-0 lh-lg">{item.a}</p>
                </details>
              ))}
              <div className="text-center mt-4">
                <Link to="/offers/poc" className="cta-inline">
                  {t("offers.cards.0.linkLabel")} <FaArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to action: book a call, or write a few lines right here */}
      <section className="experience_section py-5 bg-light" id="contact">
        <div className="container py-4">
          <div className="row align-items-center">
            <div className="col-lg-5 mb-4 mb-lg-0">
              <div className="detail-box">
                <h2 className="font-weight-bold mb-4">{t("home.cta.title")}</h2>
                <p className="lead text-muted mb-4">
                  {t("home.cta.text")}
                </p>
                <CtaPair secondary={false} />
              </div>
            </div>
            <div className="col-lg-6 offset-lg-1">
              <QuickContact />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;